import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton } from '@shared/ui';
import { currentLocale } from '@shared/i18n';

// Browse + restore handles previously dismissed via Skip in the Pending Brands
// or Suggested Creators lists. Skipped rows live on unknown_mentions with
// status='skipped'. Brand-vs-creator tabs split by classify_kind.
// Restore = UPDATE status back to 'new'.

interface SkipRow {
  handle: string;
  note: string | null;
  decided_at: string;
  decided_by: string | null;
}

export default function SkiplistManager({ kind, search }: { kind: 'brand' | 'creator'; search: string }) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const queryKey = ['admin-skiplist', kind];
  const sourceQueryKey = kind === 'brand' ? ['admin-pending-brand-handles'] : ['admin-recommended-creators'];

  const { data, isLoading, error } = useSupabaseQuery<SkipRow[]>(
    queryKey,
    async () => {
      const q = supabase
        .from('unknown_mentions')
        .select('handle, note, decided_at, decided_by')
        .eq('status', 'skipped')
        .order('decided_at', { ascending: false })
        .limit(500);
      // Brand tab: everything not classified as blogger. Creator tab: only bloggers.
      return kind === 'creator'
        ? q.eq('classify_kind', 'blogger')
        : q.or('classify_kind.neq.blogger,classify_kind.is.null');
    },
  );

  const [restoring, setRestoring] = useState<string | null>(null);

  const filtered = (data ?? []).filter((r) =>
    !search.trim() || r.handle.toLowerCase().includes(search.trim().toLowerCase())
  );

  const onRestore = async (handle: string) => {
    setRestoring(handle);
    try {
      const { error: err } = await supabase
        .from('unknown_mentions')
        .update({ status: 'new', decided_at: null, decided_by: null })
        .eq('handle', handle);
      if (err) { alert(err.message); return; }
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: sourceQueryKey });
    } finally {
      setRestoring(null);
    }
  };

  if (isLoading) return <Skeleton height={240} />;
  if (error) return <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>;

  if (filtered.length === 0) {
    return (
      <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>
        {t('providers.skipped.empty', {
          defaultValue: kind === 'brand'
            ? 'No skipped brand handles yet.'
            : 'No skipped recommended creators yet.',
        })}
      </div>
    );
  }

  return (
    <>
      <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginBottom: 8 }}>
        {t('providers.skipped.help', {
          defaultValue: kind === 'brand'
            ? 'Handles you marked as not-a-brand. Skipped handles never reappear in the Pending tab. Click Restore if you skipped one by mistake — it returns to Pending on the next refresh.'
            : 'Handles you marked as not-a-creator. Skipped handles never reappear in the Suggested tab. Click Restore if you skipped one by mistake.',
        })}{' '}
        ({filtered.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.map((r) => {
          const isBusy = restoring === r.handle;
          return (
            <div
              key={r.handle}
              style={{
                padding: '10px 12px', borderRadius: 8,
                border: '1px solid rgba(15,45,100,0.4)',
                display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                opacity: isBusy ? 0.5 : 1,
              }}
            >
              <a
                href={`https://www.instagram.com/${r.handle}/`}
                target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, fontWeight: 'var(--fw-semibold)', color: '#bcd0ff', textDecoration: 'none', minWidth: 200 }}
                title={t('providers.openHandle', { defaultValue: 'Open on Instagram' })}
              >
                @{r.handle}
              </a>
              <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {r.note && (
                  <span style={{ fontStyle: 'italic', color: '#bdd', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.note}>
                    “{r.note}”
                  </span>
                )}
                <span style={{ color: '#778' }}>
                  {new Date(r.decided_at).toLocaleDateString(currentLocale())}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRestore(r.handle)}
                disabled={isBusy}
                style={{
                  padding: '6px 12px', background: 'transparent', color: '#5fd17a',
                  border: '1px solid rgba(95,209,122,0.4)', borderRadius: 6,
                  cursor: isBusy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)',
                }}
              >
                {isBusy ? '…' : t('providers.skipped.restore', { defaultValue: 'Restore' })}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
